import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowDownUp,
  Banknote,
  Download,
  ExternalLink,
  PiggyBank,
  Power,
  PowerOff,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useSoldes } from '@/hooks/queries/useSoldes';
import {
  useBanques,
  useBulkUpdateCaisseActive,
  useBulkUpdateCompteActive,
} from '@/hooks/queries/useBanques';
import { formatDA, formatNumberDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import type { SoldeCompteRow } from '@/lib/database.types';

export function ComptesPage(): JSX.Element {
  const canView = usePermission('comptes.view');
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: soldes = [], isLoading } = useSoldes(entrepriseId);
  const { data: banques = [] } = useBanques();
  const bulkCompte = useBulkUpdateCompteActive();
  const bulkCaisse = useBulkUpdateCaisseActive();

  const [selectedRows, setSelectedRows] = useState<SoldeCompteRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);

  const totalDzd = soldes.reduce(
    (acc, s) => acc + (s.devise_code === 'DZD' ? s.solde_courant : 0),
    0,
  );
  const totalEntrees = soldes.reduce((acc, s) => acc + s.total_entrees, 0);
  const totalSorties = soldes.reduce((acc, s) => acc + s.total_sorties, 0);

  // banques utilisées pour enrichir un éventuel libellé futur (pas utilisé pour le moment)
  void banques;

  const columns = useMemo<ColumnDef<SoldeCompteRow>[]>(
    () => [
      {
        accessorKey: 'compte_type',
        header: 'Type',
        size: 100,
        cell: ({ getValue }) => {
          const v = getValue() as 'caisse' | 'banque';
          return (
            <Badge variant="outline" className="inline-flex items-center gap-1">
              {v === 'caisse' ? (
                <PiggyBank className="h-3 w-3" />
              ) : (
                <Banknote className="h-3 w-3" />
              )}
              {v}
            </Badge>
          );
        },
      },
      { accessorKey: 'intitule', header: 'Intitulé' },
      {
        accessorKey: 'devise_code',
        header: 'Devise',
        size: 80,
        cell: ({ getValue }) => <Badge variant="outline">{String(getValue())}</Badge>,
      },
      {
        accessorKey: 'solde_initial',
        header: 'Solde initial',
        size: 130,
        cell: ({ getValue }) => <span className="num text-xs">{formatDA(Number(getValue()))}</span>,
      },
      {
        accessorKey: 'total_entrees',
        header: 'Entrées',
        size: 130,
        cell: ({ getValue }) => (
          <span className="num text-xs text-success">{formatDA(Number(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'total_sorties',
        header: 'Sorties',
        size: 130,
        cell: ({ getValue }) => (
          <span className="num text-xs text-warning">{formatDA(Number(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'solde_courant',
        header: 'Solde courant',
        size: 150,
        cell: ({ getValue }) => {
          const v = Number(getValue());
          return (
            <span className={cn('num font-semibold', v < 0 && 'text-destructive')}>
              {formatDA(v)}
            </span>
          );
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Statut',
        size: 80,
        cell: ({ getValue }) =>
          getValue() ? (
            <Badge variant="success">Actif</Badge>
          ) : (
            <Badge variant="outline">Inactif</Badge>
          ),
      },
      {
        accessorKey: 'nb_operations',
        header: 'Ops',
        size: 70,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatNumberDZ(Number(getValue()))}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => (
          <Button asChild variant="ghost" size="sm">
            <Link
              to={`/finances/operations?compte=${row.original.compte_id}&type=${row.original.compte_type}`}
              aria-label="Voir les opérations"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </Button>
        ),
      },
    ],
    [],
  );

  const selectedCaisses = selectedRows.filter((r) => r.compte_type === 'caisse');
  const selectedComptes = selectedRows.filter((r) => r.compte_type === 'banque');

  const bulkUpdateActive = async (active: boolean): Promise<void> => {
    if (selectedCaisses.length > 0) {
      await bulkCaisse.mutateAsync({
        ids: selectedCaisses.map((c) => c.compte_id),
        is_active: active,
      });
    }
    if (selectedComptes.length > 0) {
      await bulkCompte.mutateAsync({
        ids: selectedComptes.map((c) => c.compte_id),
        is_active: active,
      });
    }
    toast.success(`${selectedRows.length} compte(s) ${active ? 'activé(s)' : 'désactivé(s)'}`);
    clearSelection();
  };

  if (!canView) return <AccessDenied feature="comptes.view" />;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('finances.comptes.title')}
        subtitle={t('finances.comptes.subtitle')}
        actions={
          <select
            value={entrepriseId}
            onChange={(e) => setEntrepriseId(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {entreprises.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <KpiCard
          icon={<PiggyBank className="h-5 w-5" />}
          label={t('finances.tresorerie.kpiSoldeTotal')}
          value={formatDA(totalDzd)}
          tone={totalDzd >= 0 ? 'primary' : 'destructive'}
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" />}
          label={t('finances.comptes.totalEntrees')}
          value={formatDA(totalEntrees)}
          tone="success"
        />
        <KpiCard
          icon={<TrendingDown className="h-5 w-5" />}
          label={t('finances.comptes.totalSorties')}
          value={formatDA(totalSorties)}
          tone="warning"
        />
      </div>

      <DataTable
        key={selectionVersion}
        data={soldes}
        columns={columns}
        loading={isLoading}
        pageSize={20}
        searchPlaceholder="Rechercher un compte…"
        exportFileName="comptes.csv"
        enableSelection
        getRowId={(row) => `${row.compte_type}:${row.compte_id}`}
        onSelectionChange={setSelectedRows}
        emptyMessage="Aucun compte ou caisse pour cette entreprise."
        bulkBar={
          <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
            <BulkButton onClick={() => exportComptesCSV(selectedRows)}>
              <Download className="h-3.5 w-3.5" /> Exporter CSV
            </BulkButton>
            <BulkButton onClick={() => void bulkUpdateActive(true)}>
              <Power className="h-3.5 w-3.5" /> Activer
            </BulkButton>
            <BulkButton onClick={() => void bulkUpdateActive(false)}>
              <PowerOff className="h-3.5 w-3.5" /> Désactiver
            </BulkButton>
          </BulkActionBar>
        }
      />

      <p className="text-xs text-muted-foreground">
        <ArrowDownUp className="mr-1 inline h-3 w-3" />
        Les comptes/caisses ne peuvent pas être supprimés en masse (donnée critique). Pour supprimer
        un compte vide, utilisez le CRUD dans Paramètres &gt; Banques &amp; caisses.
      </p>
    </div>
  );
}

function exportComptesCSV(rows: SoldeCompteRow[]): void {
  if (rows.length === 0) return;
  const headers = [
    'compte_type',
    'intitule',
    'devise_code',
    'solde_initial',
    'total_entrees',
    'total_sorties',
    'solde_courant',
    'is_active',
    'nb_operations',
  ];
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = (r as unknown as Record<string, unknown>)[h];
          const s = v == null ? '' : String(v);
          return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `comptes-${rows.length}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`${rows.length} compte(s) exporté(s)`);
}

interface KpiCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: 'primary' | 'success' | 'warning' | 'destructive';
}

function KpiCard({ label, value, icon, tone = 'primary' }: KpiCardProps): JSX.Element {
  const toneCls: Record<NonNullable<KpiCardProps['tone']>, string> = {
    primary: 'text-primary bg-primary/10',
    success: 'text-success bg-success/10',
    warning: 'text-warning bg-warning/10',
    destructive: 'text-destructive bg-destructive/10',
  };
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className={cn('rounded-md p-1.5', toneCls[tone])}>{icon}</div>
      </div>
      <div className="num mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}
